import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { diagnosticsStore } from './diagnostic-store'
import type { DiagnosticAction, DiagnosticEvent } from './diagnostic-types'
import { sanitizeResponseBody, sanitizeText, sanitizeUrl } from './format-diagnostic'
import { testSessionStore } from '../test-session/test-session-store'
import type { TestSessionAction } from '../test-session/test-session-types'

type DiagnosticClient = NonNullable<DiagnosticEvent['request']>['client']
type ActionContext = ReturnType<typeof diagnosticsStore.getActionContext>

type AxiosFailure = {
  config?: Pick<InternalAxiosRequestConfig, 'method' | 'url' | 'data'>
  message?: string
  name?: string
  stack?: string
  response?: {
    data?: unknown
    headers?: Record<string, unknown>
    status?: number
  }
}

type AxiosSuccess = {
  status: number
  data?: unknown
  headers?: Record<string, unknown>
}

let eventSequence = 0

function isCredentialExchange(url?: string): boolean {
  if (!url) return false
  try {
    return /^\/supplier\/api\/v1\/web\/(auth\/login|legacy\/(reauth|launch)|admission\/login\/(password|code))$/.test(new URL(url, window.location.origin).pathname)
  } catch { return false }
}

function createId(prefix: string): string {
  eventSequence += 1
  return `${prefix}-${Date.now()}-${eventSequence}`
}

function getBuildInfo(): DiagnosticEvent['build'] {
  const buildInfo = (window as Window & { __BUILD_INFO__?: DiagnosticEvent['build'] }).__BUILD_INFO__
  return buildInfo ?? { commit: 'dev', mode: 'development' }
}

function getPageUrl(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined
}

function getResponseField(data: unknown, field: string): string | undefined {
  if (!data || typeof data !== 'object') return undefined
  return getStringValue((data as Record<string, unknown>)[field])
}

function getBusinessOutcome(data: unknown): 'success' | 'failure' | undefined {
  if (!data || typeof data !== 'object') return undefined
  const envelope = data as Record<string, unknown>
  const explicitSuccess = envelope.success ?? envelope.ok
  if (typeof explicitSuccess === 'boolean') return explicitSuccess ? 'success' : 'failure'

  const code = getResponseField(data, 'code')?.trim()
  if (!code) return undefined
  const normalizedCode = code.toUpperCase()
  if (['0', 'OK', 'SUCCESS', 'TRUE'].includes(normalizedCode)) return 'success'
  if (/^-?\d+$/.test(normalizedCode)) {
    const numericCode = Number(normalizedCode)
    return numericCode >= 200 && numericCode < 300 ? 'success' : 'failure'
  }
  return /^(FAIL|FAILED|ERROR|NO_|DENIED|INVALID)/.test(normalizedCode) ? 'failure' : undefined
}

function getHeader(headers: Record<string, unknown> | undefined, name: string): string | undefined {
  if (!headers) return undefined
  const key = Object.keys(headers).find((candidate) => candidate.toLowerCase() === name)
  return key ? getStringValue(headers[key]) : undefined
}

function describeValue(value: unknown): string {
  if (Array.isArray(value)) return `array(${value.length})`
  if (value === null) return 'null'
  if (typeof value === 'object') return 'object'
  return typeof value
}

function describeRequestBody(data: unknown): string[] | undefined {
  let value = data
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value)
    } catch {
      return undefined
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  if (value instanceof FormData) return Array.from(value.keys()).slice(0, 20).map((key) => `${key}:form-data`)
  if (value instanceof URLSearchParams) return Array.from(new Set(value.keys())).slice(0, 20).map((key) => `${key}:string`)

  return Object.entries(value as Record<string, unknown>)
    .slice(0, 20)
    .map(([key, item]) => `${key}:${describeValue(item)}`)
}

function describeRequestInput(config: AxiosFailure['config']): NonNullable<DiagnosticEvent['request']>['input'] {
  let queryKeys: string[] | undefined
  if (config?.url) {
    try {
      queryKeys = Array.from(new Set(Array.from(new URL(config.url, window.location.origin).searchParams.keys()))).slice(0, 20)
    } catch {
      queryKeys = undefined
    }
  }
  const body = describeRequestBody(config?.data)
  return queryKeys?.length || body?.length ? { queryKeys, body } : undefined
}

function getRequestCategory(error: AxiosFailure): NonNullable<DiagnosticEvent['request']>['category'] {
  if (!error.response) return /timeout|ECONNABORTED/i.test(error.message ?? '') ? 'timeout' : 'network'
  return (error.response.status ?? 0) >= 500 ? 'server' : 'client'
}

function findRequestTrigger(actions: TestSessionAction[], startedAt: number): TestSessionAction | undefined {
  for (let index = actions.length - 1; index >= 0; index -= 1) {
    const action = actions[index]
    if (action.at > startedAt) continue
    if (startedAt - action.at <= 10_000) return action
  }
  return undefined
}

function recordTestSessionRequest(
  client: DiagnosticClient,
  startedAt: number,
  config: AxiosFailure['config'],
  response: AxiosSuccess | AxiosFailure['response'],
  outcome: 'success' | 'failure',
  at: number,
): void {
  const snapshot = testSessionStore.getSnapshot()
  if (!snapshot.active) return
  const data = response?.data
  const headers = response?.headers
  const responseCode = getResponseField(data, 'code')
  const responseMessage = getResponseField(data, 'message')
  const businessOutcome = getBusinessOutcome(data)
  const traceId = getResponseField(data, 'traceId') ?? getHeader(headers, 'x-trace-id') ?? getHeader(headers, 'x-request-id')
  const input = describeRequestInput(config)
  const trigger = findRequestTrigger(snapshot.actions, startedAt)
  testSessionStore.recordRequest({
    at,
    route: getPageUrl(),
    client,
    method: (config?.method ?? 'GET').toUpperCase(),
    path: sanitizeUrl(config?.url ?? '未知请求地址'),
    outcome: outcome === 'failure' || businessOutcome === 'failure' ? 'failure' : 'success',
    status: response?.status,
    durationMs: Math.max(0, at - startedAt),
    responseCode: responseCode ? sanitizeText(responseCode) : undefined,
    responseMessage: responseMessage ? sanitizeText(responseMessage) : undefined,
    businessOutcome,
    traceId: traceId ? sanitizeText(traceId) : undefined,
    input: input && { queryKeys: input.queryKeys?.map(sanitizeText), body: input.body?.map(sanitizeText) },
    trigger,
  })
}

function recordTestSessionDiagnostic(event: DiagnosticEvent): void {
  testSessionStore.recordDiagnostic({
    id: event.id,
    at: event.at,
    kind: event.kind,
    route: event.pageUrl,
    correlation: event.correlation,
    error: { name: event.error.name, message: event.error.message },
    request: event.request && {
      client: event.request.client,
      method: event.request.method,
      path: event.request.path,
      status: event.request.status,
      durationMs: event.request.durationMs,
      responseCode: event.request.responseCode,
      responseMessage: event.request.responseMessage,
      businessOutcome: event.request.businessOutcome,
      traceId: event.request.traceId,
      category: event.request.category,
      input: event.request.input,
    },
  })
}

function recordAxiosBusinessFailure(
  client: DiagnosticClient,
  startedAt: number,
  config: AxiosFailure['config'],
  response: AxiosSuccess,
  at: number,
  actionContext: ActionContext,
): void {
  if (getBusinessOutcome(response.data) !== 'failure') return
  const data = response.data
  const responseCode = getResponseField(data, 'code')
  const responseMessage = getResponseField(data, 'message')
  const diagnostic: DiagnosticEvent = {
    id: createId('business'),
    at,
    kind: 'request',
    pageUrl: getPageUrl(),
    build: getBuildInfo(),
    ...actionContext,
    error: { name: 'BusinessError', message: responseMessage ?? `业务失败${responseCode ? `（${responseCode}）` : ''}` },
    request: {
      client,
      method: (config?.method ?? 'GET').toUpperCase(),
      path: config?.url ?? '未知请求地址',
      status: response.status,
      durationMs: Math.max(0, at - startedAt),
      responseCode,
      responseMessage,
      responseBody: typeof data === 'undefined' ? undefined : sanitizeResponseBody(data),
      traceId: getResponseField(data, 'traceId') ?? getHeader(response.headers, 'x-trace-id') ?? getHeader(response.headers, 'x-request-id'),
      category: 'business',
      input: describeRequestInput(config),
      businessOutcome: 'failure',
    },
  }
  diagnosticsStore.recordEvent(diagnostic)
  recordTestSessionDiagnostic(diagnostic)
}

function describeActionTarget(target: Element): DiagnosticAction['element'] {
  const control = target.closest<HTMLElement>('[data-testid], button, a, input, select, textarea, [role="button"], [role="option"], .ant-select-item-option')
    ?? (target instanceof HTMLElement ? target : null)
  const component = target.closest<HTMLElement>('[data-c]')

  return {
    tag: control?.tagName.toLowerCase() ?? target.tagName.toLowerCase(),
    testId: control?.dataset.testid,
    dataComponent: component?.dataset.c,
    role: control?.getAttribute('role') ?? undefined,
  }
}

function getActionLabel(target: Element): string | undefined {
  const control = target.closest<HTMLElement>('[aria-label], button, a, input, select, textarea, [role="button"], [role="option"], .ant-select-item-option')
    ?? (target instanceof HTMLElement ? target : null)
  const fieldLabel = control instanceof HTMLInputElement || control instanceof HTMLSelectElement || control instanceof HTMLTextAreaElement
    ? control.labels?.[0]?.textContent ?? control.closest('.ant-form-item')?.querySelector('.ant-form-item-label')?.textContent
    : undefined
  const selectedOptionLabel = control instanceof HTMLSelectElement ? control.selectedOptions[0]?.textContent : undefined
  const value = selectedOptionLabel
    ?? control?.getAttribute('aria-label')
    ?? control?.getAttribute('title')
    ?? fieldLabel
    ?? control?.textContent
  const label = value?.replace(/\s+/g, ' ').trim()
  if (label && label.length <= 40) return label
  return control?.dataset.testid ?? control?.closest<HTMLElement>('[data-c]')?.dataset.c
}

function isNativeSelectTarget(target: Element): boolean {
  return Boolean(target.closest('select'))
}

function isAntdSelectTrigger(target: Element): boolean {
  return !target.closest('[role="option"], .ant-select-item-option')
    && Boolean(target.closest('.ant-select-selector, .ant-select'))
}

function isDevToolsInteraction(target: Element): boolean {
  return Boolean(target.closest('[data-c="DevToolsPanel"], [data-c="TestSessionPanel"], .devtools-test-session-modal, .devtools-visual-replay-modal'))
}

function recordTestSessionAction(
  type: TestSessionAction['type'],
  target: Element,
  at = Date.now(),
  label = getActionLabel(target),
  actionTarget = describeActionTarget(target),
): void {
  testSessionStore.recordAction({ at, route: getPageUrl(), type, label, target: actionTarget })
}

function recordTestSessionRouteChange(at = Date.now()): void {
  const route = getPageUrl()
  const snapshot = testSessionStore.getSnapshot()
  if (!snapshot.active || snapshot.currentRoute === route) return
  testSessionStore.recordAction({ at, route, type: 'route', label: route })
}

function recordAction(
  type: DiagnosticAction['type'],
  target: Element,
  options: { sessionType?: TestSessionAction['type']; recordTestSession?: boolean } = {},
): void {
  const at = Date.now()
  const label = getActionLabel(target)
  const element = describeActionTarget(target)
  const action = {
    id: createId('action'),
    at,
    type,
    label,
    element,
  }
  diagnosticsStore.recordAction(action)
  if (options.recordTestSession !== false) {
    recordTestSessionAction(options.sessionType ?? (target.closest('[role="option"], option, .ant-select-item-option') ? 'select' : type), target, at, label, element)
  }
}

function recordRuntimeError(reason: unknown): void {
  const error = reason instanceof Error
    ? reason
    : new Error(typeof reason === 'string' ? reason : '未知运行时错误')
  const at = Date.now()
  const diagnostic: DiagnosticEvent = {
    id: createId('runtime'),
    at,
    kind: 'runtime',
    pageUrl: getPageUrl(),
    build: getBuildInfo(),
    ...diagnosticsStore.getActionContext(at),
    error: { name: error.name, message: error.message, stack: error.stack },
  }
  diagnosticsStore.recordEvent(diagnostic)
  recordTestSessionDiagnostic(diagnostic)
}

export function startRuntimeDiagnostics(): () => void {
  const handleClick = (event: MouseEvent) => {
    if (event.button !== 0 || !(event.target instanceof Element) || isDevToolsInteraction(event.target) || isAntdSelectTrigger(event.target)) return
    recordAction('click', event.target, isNativeSelectTarget(event.target) ? { recordTestSession: false } : undefined)
  }
  const handleKeydown = (event: KeyboardEvent) => {
    if (event.isComposing || !['Enter', ' '].includes(event.key) || !(event.target instanceof Element) || isDevToolsInteraction(event.target)) return
    recordAction('keyboard', event.target, isNativeSelectTarget(event.target) ? { recordTestSession: false } : undefined)
  }
  const handleChange = (event: Event) => {
    if (!(event.target instanceof HTMLSelectElement) || isDevToolsInteraction(event.target)) return
    recordTestSessionAction('select', event.target)
  }
  const handleError = (event: ErrorEvent) => recordRuntimeError(event.error ?? event.message)
  const handleRejection = (event: PromiseRejectionEvent) => recordRuntimeError(event.reason)
  const handleRouteChange = () => recordTestSessionRouteChange()
  const originalPushState = window.history.pushState.bind(window.history)
  const originalReplaceState = window.history.replaceState.bind(window.history)
  const trackedPushState: History['pushState'] = (...args) => {
    originalPushState.apply(window.history, args)
    window.dispatchEvent(new Event('nsy:route-change'))
  }
  const trackedReplaceState: History['replaceState'] = (...args) => {
    originalReplaceState.apply(window.history, args)
    window.dispatchEvent(new Event('nsy:route-change'))
  }

  document.addEventListener('click', handleClick, true)
  document.addEventListener('keydown', handleKeydown, true)
  document.addEventListener('change', handleChange, true)
  window.addEventListener('error', handleError)
  window.addEventListener('unhandledrejection', handleRejection)
  window.addEventListener('popstate', handleRouteChange)
  window.addEventListener('hashchange', handleRouteChange)
  window.addEventListener('nsy:route-change', handleRouteChange)
  window.history.pushState = trackedPushState
  window.history.replaceState = trackedReplaceState

  return () => {
    document.removeEventListener('click', handleClick, true)
    document.removeEventListener('keydown', handleKeydown, true)
    document.removeEventListener('change', handleChange, true)
    window.removeEventListener('error', handleError)
    window.removeEventListener('unhandledrejection', handleRejection)
    window.removeEventListener('popstate', handleRouteChange)
    window.removeEventListener('hashchange', handleRouteChange)
    window.removeEventListener('nsy:route-change', handleRouteChange)
    if (window.history.pushState === trackedPushState) window.history.pushState = originalPushState
    if (window.history.replaceState === trackedReplaceState) window.history.replaceState = originalReplaceState
  }
}

export function recordAxiosFailure(
  client: DiagnosticClient,
  startedAt: number,
  error: AxiosFailure,
  at = Date.now(),
  actionContext: ActionContext = diagnosticsStore.getActionContext(at)
): void {
  if (isCredentialExchange(error.config?.url)) return
  const data = error.response?.data
  const headers = error.response?.headers
  const diagnostic: DiagnosticEvent = {
    id: createId('request'),
    at,
    kind: 'request',
    pageUrl: getPageUrl(),
    build: getBuildInfo(),
    ...actionContext,
    error: {
      name: error.name ?? 'AxiosError',
      message: error.message ?? '请求失败',
      stack: error.stack,
    },
    request: {
      client,
      method: (error.config?.method ?? 'GET').toUpperCase(),
      path: error.config?.url ?? '未知请求地址',
      status: error.response?.status,
      durationMs: Math.max(0, at - startedAt),
      responseCode: getResponseField(data, 'code'),
      responseMessage: getResponseField(data, 'message'),
      responseBody: typeof data === 'undefined' ? undefined : sanitizeResponseBody(data),
      traceId: getResponseField(data, 'traceId') ?? getHeader(headers, 'x-trace-id') ?? getHeader(headers, 'x-request-id'),
      category: getRequestCategory(error),
      input: describeRequestInput(error.config),
    },
  }
  diagnosticsStore.recordEvent(diagnostic)
  recordTestSessionDiagnostic(diagnostic)
  recordTestSessionRequest(client, startedAt, error.config, error.response, 'failure', at)
}

export function recordAxiosSuccess(
  client: DiagnosticClient,
  startedAt: number,
  config: AxiosFailure['config'],
  response: AxiosSuccess,
  at = Date.now(),
  actionContext: ActionContext = diagnosticsStore.getActionContext(at),
): void {
  if (isCredentialExchange(config?.url)) return
  recordTestSessionRequest(client, startedAt, config, response, 'success', at)
  recordAxiosBusinessFailure(client, startedAt, config, response, at, actionContext)
}

export function installAxiosDiagnostics(client: AxiosInstance, clientName: DiagnosticClient): void {
  const metadataByConfig = new WeakMap<InternalAxiosRequestConfig, { startedAt: number; actionContext: ActionContext }>()

  client.interceptors.request.use((config) => {
    const startedAt = Date.now()
    metadataByConfig.set(config, { startedAt, actionContext: diagnosticsStore.getActionContext(startedAt) })
    return config
  })
  client.interceptors.response.use(
    (response) => {
      const metadata = metadataByConfig.get(response.config)
      recordAxiosSuccess(clientName, metadata?.startedAt ?? Date.now(), response.config, response, Date.now(), metadata?.actionContext)
      return response
    },
    (error: unknown) => {
      if (axios.isCancel(error)) {
        // Preserve the cancellation behavior expected by existing HTTP clients.
        return Promise.reject(error)
      }
      const failure = error as AxiosFailure
      const config = (error as { config?: InternalAxiosRequestConfig }).config
      if (config) {
        const metadata = metadataByConfig.get(config)
        recordAxiosFailure(clientName, metadata?.startedAt ?? Date.now(), failure, Date.now(), metadata?.actionContext)
      }
      // Preserve the original Axios rejection for the existing error interceptor.
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject(error)
    }
  )
}
